/**
 * Whether reopening the cancel chat resumes the conversation already in state or
 * starts a new one.
 *
 * The chat component stays MOUNTED across close/reopen — `open` is only a prop —
 * so the transcript, the offer cards and the intake refs all survive on their own.
 * Nothing needs to be stored anywhere. Resuming is simply the act of not wiping
 * them, which is why this is a decision rather than a persistence layer, and why
 * it lives here as a pure function instead of inside the reset effect.
 *
 * A page reload starts fresh, by design: the component is new, so its state is too.
 */

export interface ResumeDecision {
  /** Identity of the conversation currently held in state; null before the first. */
  conversationKey: string | null;
  /** Identity of the conversation being opened now — see conversationKey(). */
  currentKey: string;
  /** Non-null once they cancelled / took the offer / switched to one-time. */
  success: unknown;
  /** True once the chat has hit a fatal error and fallen back. */
  fatal: boolean;
}

/**
 * Identify a conversation by the thing it is ABOUT. The manage sheet can be opened
 * for a different subscription within one page session, and one product's
 * conversation must never surface under another's — that is the whole reason this
 * is keyed rather than a bare boolean.
 *
 * Serialised rather than interpolated: a plain `${id}:${productId}` lets a missing
 * id and a literal id spelled like the placeholder collide into the same key.
 */
export function conversationKey(
  subscriptionId: string | number | null | undefined,
  productId: string | number,
): string {
  return JSON.stringify([subscriptionId ?? null, productId]);
}

/**
 * Reset when the conversation in state is spent or belongs to something else:
 *
 * - a DIFFERENT subscription/product — resuming there would show the customer a
 *   transcript about a product they aren't cancelling;
 * - a TERMINAL outcome — they cancelled or switched to one-time, so the
 *   subscription has changed underneath the transcript;
 * - a FATAL error — the chat already fell back; don't resurrect a broken thread.
 *
 * Otherwise resume: they closed the sheet mid-conversation and came back.
 */
export function shouldResetConversation(d: ResumeDecision): boolean {
  if (d.conversationKey !== d.currentKey) return true;
  if (d.success != null) return true;
  if (d.fatal) return true;
  return false;
}
